// Shared Date Utilities
// Treat database DATE columns as date-only keys (YYYY-MM-DD), no time or timezone.

// Returns YYYY-MM-DD for inputs that are already date keys, or derives from Date
export function toDateKey(input: string | Date | null | undefined): string | null {
  if (input == null) return null;
  if (typeof input === "string") {
    const m = input.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return input;
    // Attempt to coerce other strings into a date key using local parts
    const d = new Date(input);
    if (Number.isFinite(d.getTime())) return fromLocalDate(d);
    return null;
  }
  return fromLocalDate(input);
}

// Build YYYY-MM-DD from a Date using local calendar components
export function fromLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Today as YYYY-MM-DD in local calendar
export function todayKey(): string {
  return fromLocalDate(new Date());
}

// Compare two YYYY-MM-DD date keys
export function compareDateKeys(a: string, b: string): number {
  return a.localeCompare(b);
}

// Check if dateKey is within [fromKey, toKey] (inclusive). Empty bounds are ignored.
export function isInDateRange(
  dateKey: string,
  fromKey?: string | null,
  toKey?: string | null,
): boolean {
  if (fromKey && compareDateKeys(dateKey, fromKey) < 0) return false;
  if (toKey && compareDateKeys(dateKey, toKey) > 0) return false;
  return true;
}

// Select the minimum (earliest) date key from a list. Returns null for empty lists.
export function minDateKey(keys: (string | null | undefined)[]): string | null {
  const vals = keys.filter((k): k is string => !!k);
  if (vals.length === 0) return null;
  return vals.reduce((min, k) => (compareDateKeys(k, min) < 0 ? k : min), vals[0]);
}

// Select the maximum (latest) date key from a list. Returns null for empty lists.
export function maxDateKey(keys: (string | null | undefined)[]): string | null {
  const vals = keys.filter((k): k is string => !!k);
  if (vals.length === 0) return null;
  return vals.reduce((max, k) => (compareDateKeys(k, max) > 0 ? k : max), vals[0]);
}

